import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";

/* ------------------------------------------------------------------ *
 * Notifications + work-order transfers — API client.
 *
 * Server contract: docs/notifications-frontend-handoff.md in the server
 * repo. Notifications use the `{ success, data }` envelope; transfers use
 * the work-order `{ status, response, message, details }` envelope. Both
 * are scoped to the open business by the `business-id` header that
 * axiosConfig attaches to every request.
 * ------------------------------------------------------------------ */

const N = `${BASE_SERVER_URL}/notifications`;
const T = `${BASE_SERVER_URL}/work-orders/transfers`;

const unwrapSuccess = (res) => {
  const body = (res && res.data) || {};
  if (!body.success) throw new Error(body.message || "Request failed");
  return body.data || {};
};

const unwrapStatus = (res) => {
  const body = (res && res.data) || {};
  if (body.status !== "success") throw new Error(body.message || "Request failed");
  return body.response;
};

/* ---- notifications ---- */

/** Newest notification (+ the unviewed count). `unviewedOnly` limits `item`
    to the newest one nobody has viewed yet — the badge poll. */
export const fetchLatest = (unviewedOnly = false) =>
  axios
    .get(`${N}/latest`, { params: unviewedOnly ? { unviewed_only: true } : {} })
    .then(unwrapSuccess);

export const fetchUnviewedCount = () =>
  axios.get(`${N}/unviewed-count`).then(unwrapSuccess);

/** Paged feed, newest first. `viewed` true/false/undefined, `type` exact. */
export const fetchNotifications = ({ viewed, type, page = 1, limit = 50 } = {}) => {
  const params = { page, limit };
  if (viewed === true || viewed === false) params.viewed = viewed;
  if (type) params.type = type;
  return axios.get(N, { params }).then(unwrapSuccess);
};

export const markViewed = (id) =>
  axios.post(`${N}/${encodeURIComponent(id)}/viewed`).then(unwrapSuccess);

export const markManyViewed = (ids) =>
  axios.post(`${N}/viewed`, { ids }).then(unwrapSuccess);

export const markAllViewed = () =>
  axios.post(`${N}/viewed`, { all: true }).then(unwrapSuccess);

export const markUnviewed = (ids) =>
  axios.post(`${N}/unviewed`, { ids }).then(unwrapSuccess);

/* ---- work-order transfers ---- */

/** Businesses this one may send an order to — every active business in the
    group except the open one. `[{ id, name, code }]`. */
export const fetchTransferDestinations = () =>
  axios.get(`${T}/destinations`).then(unwrapStatus);

/** Ask another business to take an order. Rejects with the axios error; a
    409 carries `error.response.data.details.transfer_id` — the request that
    is already awaiting approval. */
export const requestTransfer = ({ work_order_id, to_business_id, note }) =>
  axios
    .post(`${T}/request`, {
      work_order_id,
      to_business_id,
      ...(note ? { note } : {}),
    })
    .then(unwrapStatus);

export const fetchTransfer = (id) =>
  axios.get(`${T}/${encodeURIComponent(id)}`).then(unwrapStatus);

export const fetchTransfers = ({ direction, status } = {}) => {
  const params = {};
  if (direction) params.direction = direction;
  if (status) params.status = status;
  return axios.get(T, { params }).then(unwrapStatus);
};

/** Accept. Resolves with the updated transfer; rejects with the axios
    error — a 409 carries `error.response.data.details` naming what blocks
    the move and which flag lifts it. */
export const acceptTransfer = (id, body = {}) =>
  axios.post(`${T}/${encodeURIComponent(id)}/accept`, body).then(unwrapStatus);

export const rejectTransfer = (id, body = {}) =>
  axios.post(`${T}/${encodeURIComponent(id)}/reject`, body).then(unwrapStatus);

/** Withdraw our own request. Source side only, only while pending — a 404
    means it was already decided (or we are not the source). */
export const cancelTransfer = (id, body = {}) =>
  axios.post(`${T}/${encodeURIComponent(id)}/cancel`, body).then(unwrapStatus);

/** The pending outgoing request for one order, or null. The list is small
    and unpaged, so one fetch answers "is this order on its way out?". */
export const pendingTransferFor = (workOrderId) =>
  fetchTransfers({ direction: "outgoing", status: "pending" }).then((list) =>
    (Array.isArray(list) ? list : []).find(
      (t) => String(t.work_order_id) === String(workOrderId)
    ) || null
  );

/* ---- helpers shared by the bell, feed and transfer screens ---- */

/** Where a notification takes the user, by its subject. Null = nowhere. */
export function routeForNotification(n) {
  const subject = (n && n.subject) || {};
  if (subject.kind === "work_order_transfer" && subject.id) {
    return `/transfers/${subject.id}`;
  }
  return null;
}

/** Human label for a notification type; unknown types fall back to the
    type string itself, so a new server type still renders. */
const TYPE_LABELS = {
  "work_order_transfer.requested": "Transfer request",
};
export const typeLabel = (type) =>
  TYPE_LABELS[type] || String(type || "").replace(/[._]/g, " ") || "Notification";

export const KNOWN_TYPES = Object.keys(TYPE_LABELS);
